import { CloseOutlined } from '@ant-design/icons';
import { Button } from '@mm/components';
import { useAudiencesCRUDActions } from '@mm/state/actions/AudiencesCRUDActions';
import {
  MyLeadsFilterTypes,
  MyLeadsSearchQuery,
  SubsectionKeys,
} from '@mm/state/interfaces/myLeads';
import AudiencesModel from '@mm/state/model/AudiencesModel';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { MODALS_TYPE } from '@mm/state/modules/modalsState/types';
import { selectAudiences } from '@mm/state/selectors/messengerAdSelectors';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import { Spin } from 'antd';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { FilterByLandingPage } from '../../../editor/features/beta/modules/Contacts/FilterByLandingPage/FilterByLandingPage';
import { useCreateMyLeadsAudience } from '../../../editor/features/beta/modules/Contacts/hooks/use-create-my-leads-audience';
import { prepareEsFiltersStructure } from '../../../editor/features/beta/modules/Contacts/MyLeadsFiltersPanel/helpers';
import { useCreateFilterParams } from '../../../editor/features/beta/modules/Contacts/use-create-filter-params';
import styles from './ConversionURLModal.module.css';

export const ConversionURLModal = ({
  audience,
  setFilterParams: setFilterParamsProps,
}: {
  audience?: AudiencesModel;
  setFilterParams?: (params: MyLeadsSearchQuery) => void;
}) => {
  const botId = useSelector(activeChatbotIdSelector);
  const audiences = useSelector(selectAudiences);
  const defaultAudience =
    audience || audiences.find(a => a.type === 'converted_leads');
  const { filterParams, setFilterParams } = useCreateFilterParams(
    defaultAudience?.esFilters
      ? {
          query: prepareEsFiltersStructure(defaultAudience?.esFilters) || {},
        }
      : undefined,
  );
  const { closeModal } = useModalActions();
  const { createMyLeadsAudience } = useCreateMyLeadsAudience();
  const { retrieveAll, update } = useAudiencesCRUDActions();

  const [busy, setBusy] = useState(false);

  const hasFilterParams = Object.keys(filterParams.query || {}).length;

  const retrieveConvertedLeadsAudiences = async () => {
    setBusy(true);
    await retrieveAll(botId, 1, 25, undefined, true, {
      skipBusy: true,
      type: 'converted_leads',
    });
    setBusy(false);
  };

  const createOrUpdateAudience = async () => {
    if (!defaultAudience) {
      await createMyLeadsAudience({
        params: {
          esFilters: filterParams.query,
          name: 'Converted Leads',
          type: 'converted_leads',
        },
      });
    } else {
      update({ ...defaultAudience, esFilters: filterParams.query }, botId);
      setFilterParamsProps?.({ query: filterParams.query });
    }

    closeModal(MODALS_TYPE.CONVERSION_URL_MODAL);
  };

  useEffect(() => {
    if (!audience) {
      retrieveConvertedLeadsAudiences();
    }
  }, []);

  return (
    <Spin spinning={busy}>
      <div className={styles.container}>
        <CloseOutlined
          className={styles.closeIcon}
          onClick={() => closeModal()}
        />
        <div className={styles.header}>
          <div className={styles.title}>Add Conversion URL(s)</div>
          <div className={styles.subTitle}>to help track attribution</div>
        </div>
        <FilterByLandingPage
          attributeKey={SubsectionKeys.urlsVisited}
          title=""
          filterParams={filterParams}
          setFilterParams={setFilterParams}
          type={MyLeadsFilterTypes.attributes}
          className={styles.urlBlock}
          defaultOperatorType="wildcard"
          isLowercasedKey
          hideExclude
        />

        <div className={styles.buttons}>
          <Button
            css={{ container: styles.cancelButton }}
            color="transparent-no-shadow"
            onClick={() => closeModal()}
          >
            Cancel
          </Button>
          <Button
            css={{ container: styles.button }}
            onClick={createOrUpdateAudience}
            disabled={!hasFilterParams || busy}
          >
            Save
          </Button>
        </div>
      </div>
    </Spin>
  );
};
